import React, { useCallback, useEffect, useState } from "react";
import { Mail, Phone, Printer, ShoppingCart, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useCamp, logoUrl } from "@/context/SettingsContext";
import { Panel } from "@/pages/shared";

const printOrder = (group, camp, title, logo) => {
  const w = window.open("", "_blank");
  if (!w) { toast.error("Sta pop-ups toe om de bestelling af te drukken"); return; }
  const d = w.document;
  d.title = `${title} — ${group.supplier_name || ""}`;
  d.body.setAttribute("style", "font-family:Arial,sans-serif;font-size:13px;color:#1e293b;padding:24px");

  const el = (tag, style, text) => {
    const n = d.createElement(tag);
    if (style) n.setAttribute("style", style);
    if (text !== undefined) n.textContent = String(text ?? "");
    return n;
  };

  const img = el("img", "height:64px;max-width:220px;object-fit:contain;display:block;margin-bottom:12px");
  img.src = logo;
  img.alt = "";
  d.body.appendChild(img);
  d.body.appendChild(el("h2", null, `${title} — ${group.supplier_name || ""}`));

  const addr = el("p");
  [camp.camp_name, `${camp.street || ""} ${camp.postal_city || ""}`.trim(),
    [camp.phone, camp.email].filter(Boolean).join(" · ")]
    .filter(Boolean).forEach((line, i) => {
      if (i) addr.appendChild(d.createElement("br"));
      addr.appendChild(d.createTextNode(line));
    });
  d.body.appendChild(addr);

  const table = el("table", "border-collapse:collapse;min-width:520px");
  table.setAttribute("cellpadding", "6");
  const head = el("tr", "background:#f1f5f9");
  [["Code", "left"], ["Artikel", "left"], ["Aantal", "right"], ["Eenheid", "left"], ["Voorraad / min", "right"]]
    .forEach(([label, align]) => {
      const th = el("th", null, label);
      th.setAttribute("align", align);
      head.appendChild(th);
    });
  table.appendChild(head);
  group.lines.forEach((l) => {
    const tr = d.createElement("tr");
    tr.appendChild(el("td", null, l.code));
    tr.appendChild(el("td", null, l.name));
    const qty = el("td", "text-align:right");
    qty.appendChild(el("b", null, l.qty));
    tr.appendChild(qty);
    tr.appendChild(el("td", null, l.unit));
    tr.appendChild(el("td", "text-align:right;color:#64748b", `${l.stock} / ${l.min_stock}`));
    table.appendChild(tr);
  });
  d.body.appendChild(table);
  d.body.appendChild(el("p", "color:#64748b;font-size:11px", new Date().toLocaleString()));

  w.focus();
  w.print();
};

export default function OrderAdvice({ refreshKey }) {
  const { t } = useLang();
  const { camp } = useCamp();
  const [groups, setGroups] = useState(null);
  const [mailFor, setMailFor] = useState(null);
  const [mail, setMail] = useState({ to: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setGroups((await api.get("/stock/order-advice")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setGroups([]); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const openMail = (g) => {
    setMailFor(g);
    setMail({
      to: g.email || "",
      subject: `${t("order_advice")} — ${camp.camp_name || ""}`,
      message: `Beste${g.contact ? " " + g.contact : ""},\n\nGraag onderstaande artikelen leveren.\n\nMet vriendelijke groeten,\n${camp.camp_name || ""}`,
    });
  };

  const sendMail = async () => {
    setBusy(true);
    try {
      await api.post("/stock/order/mail", {
        supplier_id: mailFor.supplier_id, to: mail.to, subject: mail.subject, message: mail.message,
        lines: mailFor.lines.map((l) => ({ code: l.code, name: l.name, unit: l.unit, qty: l.qty })),
      });
      toast.success(t("order_mail_sent"));
      setMailFor(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (groups === null) return null;

  return (
    <Panel title={t("order_advice")} testid="order-advice-panel"
      action={<span className="text-xs text-muted-foreground hidden sm:block">{t("order_advice_desc")}</span>}>
      {groups.length === 0 ? (
        <div className="p-5 text-sm text-muted-foreground text-center" data-testid="order-advice-empty">{t("order_advice_none")}</div>
      ) : (
        <div className="p-5 space-y-4">
          {groups.map((g) => (
            <div key={g.supplier_id || "none"} className="rounded-xl border border-border overflow-hidden"
              data-testid={`order-group-${g.supplier_id || "none"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/50 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="font-display font-bold text-foreground flex items-center gap-2">
                    <Truck className="h-4 w-4 text-primary" />{g.supplier_name || t("no_supplier")}
                  </p>
                  <p className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                    {g.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{g.phone}</span>}
                    {g.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{g.email}</span>}
                    {!g.email && g.supplier_id && <span className="text-amber-600">{t("order_no_email")}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => printOrder(g, camp, t("order_advice"), logoUrl(camp, "main"))}
                    data-testid={`order-print-${g.supplier_id || "none"}`}>
                    <Printer className="h-4 w-4 mr-1" />{t("order_print")}
                  </Button>
                  <Button size="sm" onClick={() => openMail(g)} disabled={!g.email}
                    data-testid={`order-mail-${g.supplier_id || "none"}`}>
                    <ShoppingCart className="h-4 w-4 mr-1" />{t("order_mail")}
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("art_code")}</TableHead>
                    <TableHead>{t("art_name")}</TableHead>
                    <TableHead className="text-right">{t("art_stock")}</TableHead>
                    <TableHead className="text-right">{t("art_min_stock")}</TableHead>
                    <TableHead className="text-right">{t("order_qty_short")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.lines.map((l) => (
                    <TableRow key={l.id} data-testid={`order-line-${l.code}`}>
                      <TableCell className="font-mono text-xs font-bold">{l.code}</TableCell>
                      <TableCell className="font-semibold text-foreground">{l.name}</TableCell>
                      <TableCell className="text-right tabular-nums text-rose-600 font-bold">{l.stock}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{l.min_stock}</TableCell>
                      <TableCell className="text-right tabular-nums font-display font-extrabold text-primary">
                        {l.qty} <span className="text-[11px] font-normal text-muted-foreground">{t(`unit_${l.unit}`)}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!mailFor} onOpenChange={(o) => !o && setMailFor(null)}>
        <DialogContent className="max-w-xl" data-testid="order-mail-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("order_mail")} — {mailFor?.supplier_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">{t("order_mail_to")}</Label>
              <Input value={mail.to} onChange={(e) => setMail({ ...mail, to: e.target.value })} data-testid="order-mail-to" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("order_mail_subject")}</Label>
              <Input value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} data-testid="order-mail-subject" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("order_mail_message")}</Label>
              <Textarea rows={5} value={mail.message} onChange={(e) => setMail({ ...mail, message: e.target.value })} data-testid="order-mail-message" /></div>
            <div className="rounded-lg bg-muted/60 px-3 py-2 text-xs space-y-1" data-testid="order-mail-preview">
              {mailFor?.lines.map((l) => (
                <div key={l.id} className="flex justify-between gap-3">
                  <span className="truncate">{l.name}</span>
                  <span className="tabular-nums font-bold">{l.qty} {t(`unit_${l.unit}`)}</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMailFor(null)} data-testid="order-mail-cancel">{t("cancel")}</Button>
            <Button onClick={sendMail} disabled={busy || !mail.to.trim()} data-testid="order-mail-send">
              <Mail className="h-4 w-4 mr-1" />{t("order_mail")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
